import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useAdmin } from "../contexts/AdminContext";
import { mockApiService, shouldUseMockData } from "../mockData/mockLocketData";
import { Eye, EyeOff, Shield } from "lucide-react";
import BrandLogo from "../components/shared/BrandLogo";

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAdmin();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm();
  const [error, setError] = React.useState(null);
  const [showPassword, setShowPassword] = React.useState(false);

  const onSubmit = async (data) => {
    setError(null);
    try {
      let response;
      if (shouldUseMockData()) {
        response = await mockApiService.adminLogin(data.username, data.password);
      } else {
        response = await api.post("/admin/login", {
          username: data.username,
          password: data.password,
        });
      }
      const { token } = response.data;
      login(token);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.error || "Invalid credentials. Please try again.");
      reset({ password: "" });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-6">
          <BrandLogo logoClassName="h-8 mb-4" />
          <div className="flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-lg px-4 py-1.5">
            <Shield size={14} className="text-slate-500" />
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">Admin Access</span>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Username
              </label>
              <input
                type="text"
                {...register("username", { required: "Username is required" })}
                className={`w-full px-3 py-2.5 border rounded-lg text-base focus:outline-none focus:ring-2 transition-colors duration-150 ${
                  errors.username
                    ? "border-red-400 focus:ring-red-200"
                    : "border-slate-300 focus:ring-red-200 focus:border-red-400"
                }`}
                placeholder="Admin username"
                autoComplete="username"
              />
              {errors.username && (
                <p className="text-red-500 text-xs mt-1 font-medium">{errors.username.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  {...register("password", { required: "Password is required" })}
                  className={`w-full px-3 py-2.5 pr-10 border rounded-lg text-base focus:outline-none focus:ring-2 transition-colors duration-150 ${
                    errors.password
                      ? "border-red-400 focus:ring-red-200"
                      : "border-slate-300 focus:ring-red-200 focus:border-red-400"
                  }`}
                  placeholder="Admin password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs mt-1 font-medium">{errors.password.message}</p>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                <p className="text-red-700 text-sm font-semibold">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold rounded-lg shadow-sm transition-colors duration-150"
            >
              {isSubmitting ? "Logging in..." : "Log In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
